/**
 * Global search — the header search box's backend call.
 *
 * Vue parity: `RW-ERP-Service/src/composables/common/global-search.ts`
 * (`fetchGlobalSearchData` → POST `/api/v1/reports/global-search` with
 * `{ query, module, size }`). With no domain filter selected Vue queries
 * `[dealerOrders, orders]` at size 4 — this module mirrors exactly that
 * default (the other Vue domains — POs, containers, product ideation —
 * are modules this React app doesn't serve).
 */
import apiClient from '../config/axios.config';
import { stringifyPrimitive as str } from '../utils/stringifyPrimitive';

/** Full wire path. Every endpoint constant in this app carries the `/api`
 *  prefix itself (`REACT_APP_API_URL` is the bare host — see `.env`). */
export const GLOBAL_SEARCH_ENDPOINT = '/api/v1/reports/global-search';

/** Wire keys — Vue `DomainKeyName` (global-search-constants.ts). */
export const GLOBAL_SEARCH_DOMAIN = {
  ORDERS: 'orders',
  STAGED_ORDERS: 'dealerOrders',
} as const;

/** Vue parity: minimum input length before a search fires. */
export const GLOBAL_SEARCH_MIN_LENGTH = 3;
/** Vue parity: the 500 ms lodash debounce on `fetchGlobalSearch`. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 500;
/** Vue parity: page size when no single domain is selected. */
const GLOBAL_SEARCH_SIZE = 4;

/** One normalized result row (an order or a staged/dealer order). */
export interface GlobalSearchHit {
  refId: string;
  /** Display number — `ecomOrderNumber` / `ecomDraftOrderNumber`. */
  number: string;
  /** Customer company name. */
  customer: string;
  /** Fulfillment status (orders) / order status (staged). */
  status: string;
  /** Payment status tag (orders `orderFinancialStatus`; staged: none).
   *  Vue parity: `getLabelProps` subState. */
  paymentStatus: string;
  /** Raw wall-clock timestamp — orders `processedAt`, staged
   *  `orderCreatedDate`. Formatted at the render site. */
  date: string;
}

export interface GlobalSearchResults {
  orders: GlobalSearchHit[];
  stagedOrders: GlobalSearchHit[];
  /** Full match counts per domain (the lists above are capped at 4). */
  counts: { orders: number; stagedOrders: number };
}

type RawHit = Record<string, unknown> | null | undefined;

/** Field mapping mirrors Vue `getLabelProps` for the two order domains. */
const mapOrderHit = (raw: RawHit): GlobalSearchHit => ({
  refId: str(raw?.refId),
  number: str(raw?.ecomOrderNumber),
  customer: str(raw?.customerEcomCompanyName),
  status: str(raw?.orderFulfillmentStatus),
  paymentStatus: str(raw?.orderFinancialStatus),
  date: str(raw?.processedAt),
});

const mapStagedHit = (raw: RawHit): GlobalSearchHit => ({
  refId: str(raw?.refId),
  number: str(raw?.ecomDraftOrderNumber),
  customer: str(raw?.customerEcomCompanyName),
  status: str(raw?.orderStatus),
  paymentStatus: '',
  date: str(raw?.orderCreatedDate),
});

export async function fetchGlobalSearch(
  query: string,
  signal?: AbortSignal,
): Promise<GlobalSearchResults> {
  const response = await apiClient.post<{
    data?: {
      results?: Record<string, RawHit[]>;
      counts?: Record<string, number>;
    };
  }>(
    GLOBAL_SEARCH_ENDPOINT,
    {
      query,
      module: [GLOBAL_SEARCH_DOMAIN.STAGED_ORDERS, GLOBAL_SEARCH_DOMAIN.ORDERS],
      size: GLOBAL_SEARCH_SIZE,
    },
    { signal },
  );

  const data = response.data?.data ?? {};
  const results = data.results ?? {};
  const counts = data.counts ?? {};

  const orders = (results[GLOBAL_SEARCH_DOMAIN.ORDERS] ?? [])
    .map(mapOrderHit)
    .filter((h) => h.refId !== '');
  const stagedOrders = (results[GLOBAL_SEARCH_DOMAIN.STAGED_ORDERS] ?? [])
    .map(mapStagedHit)
    .filter((h) => h.refId !== '');

  return {
    orders,
    stagedOrders,
    counts: {
      orders: counts[GLOBAL_SEARCH_DOMAIN.ORDERS] ?? orders.length,
      stagedOrders:
        counts[GLOBAL_SEARCH_DOMAIN.STAGED_ORDERS] ?? stagedOrders.length,
    },
  };
}
